import React, { useState, useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import selectors from '@state/selectors';
import {IProps, IState} from './types';
import * as S from './styles';
import { Context } from "routes/private/Editor/Context";
import { IDSConfig, IBrandDefinition, IBrandConfigFont } from '@state/dsconfig/types';
import {findOEM, hasAtLeastOneElementType, findCommonAttributeValue, getBrandIdByOEMName, getOEMName} from '@utils/editor';
import { cloneDeep } from 'lodash';
import { findFontByNameOrId, findOEMByNameOrId, findFontsDefinition } from '@utils/fonts';
import ToolTip from '@components/Tooltip';

const FontFamilyPicker:React.FC<IProps> = (props) => {      
    let context = useContext(Context);
    const dsConfig: IDSConfig = useSelector(selectors.dsConfig.getDSConfig);
    const [commonFonts,setCommonFonts] = useState<IBrandDefinition[] | undefined>();
    const [fontFamily,setFontFamily] = useState<IBrandConfigFont>();
    const oem = findOEM(context.entityInformation);
    const [oemFonts,setOEMFonts] = useState<IBrandDefinition[] | undefined>();
    const [notOEMFonts,setNotOEMFonts] = useState<IBrandDefinition[] | undefined>();
    const [fontsFound,setFontsFound] = useState<IBrandConfigFont[]>([]);
    const pageSize: number|undefined = 30;
    let fontsCounter = 0;
    const [resultsMaxLength,setResultsMaxLength] = useState<number|undefined>(pageSize);

    useEffect(() => {
        if (!hasAtLeastOneElementType(context.editorStatus.selectedElementsId, context.data, "Text")) {
            context.onShowPicker(undefined);
        }
        const font = findCommonAttributeValue(context.editorStatus.selectedElementsId, context.data, "fontFamily");
        if (font) setFontFamily(findFontByNameOrId(dsConfig.brands, "name", font));
    }, [context.editorStatus.selectedElementsId.length, context.editorStatus.selectedElementsId[0]]);
    
    useEffect(() => {
        if(dsConfig){
            setCommonFonts(findFontsDefinition(dsConfig.brands, "common"));
            let oemId = oem;
            if (findOEMByNameOrId(dsConfig, "id", oem ?? "")?.genericOrOther === true) oemId = undefined;
            setOEMFonts(findFontsDefinition(dsConfig.brands, oemId));
            setNotOEMFonts(findFontsDefinition(dsConfig.brands, undefined, true));
        }
    }, [dsConfig])

    const filterOEMFontsListForMaxLenght = (oemFontsList: string[]) => {
        const filteredFonts = cloneDeep(oemFontsList);
        if (filteredFonts && resultsMaxLength && resultsMaxLength<filteredFonts.length) {
          return filteredFonts.splice(0,resultsMaxLength);
        }
        return filteredFonts; 
    }

    const filterGenericFontsListForMaxLenght = (oemFontsList: IBrandDefinition[] | undefined, notoemFontsList: IBrandDefinition[] | undefined, genericFontsList: IBrandDefinition[]) => {
        let filteredFonts = cloneDeep(genericFontsList);
        const countOEMFontList = oemFontsList?oemFontsList.length:0;
        const countNotOEMFontList = notoemFontsList?notoemFontsList.length:0;
        if (resultsMaxLength) {
            if ((countOEMFontList+countNotOEMFontList)>=resultsMaxLength) {
                filteredFonts = [];
            }
            else {
                if (filteredFonts && resultsMaxLength<(filteredFonts.length+countOEMFontList+countNotOEMFontList)) {
                    return filteredFonts.splice(0,resultsMaxLength-countOEMFontList-countNotOEMFontList);
                }
            }
        }
        return filteredFonts; 
    }

    const filterFontsFoundForMaxLenght = (fonts: IBrandConfigFont[]) => {
        const filteredFonts = cloneDeep(fonts);
        if (filteredFonts && resultsMaxLength && resultsMaxLength<filteredFonts.length) {
          return filteredFonts.splice(0,resultsMaxLength);
        }
        return filteredFonts; 
    }

    const scrollEnd = (e: React.UIEvent<HTMLElement>): boolean => {
        const optionsContainer = (e.target as HTMLDivElement);
        return (optionsContainer.offsetHeight + optionsContainer.scrollTop >= optionsContainer.scrollHeight)
    }

    const optionsOnScroll = (e: React.UIEvent<HTMLElement>) => {
        if (pageSize && resultsMaxLength) {
            if (scrollEnd(e)) {
                setResultsMaxLength(resultsMaxLength! + pageSize);
            }
        }
    }

    useEffect(() => {
        setResultsMaxLength(pageSize);
    }, [props.isSearching]);
    
    useEffect(() => {
        setResultsMaxLength(pageSize);
        if (props.isSearching && props.searchValue) {
            let newOemFonts: IBrandConfigFont[] = [];
            oemFonts?.forEach(oemFont => newOemFonts = newOemFonts.concat(oemFont.fonts));
            const joinedFontsLists = (commonFonts ? commonFonts[0].fonts : []).concat(newOemFonts??[]).sort((a, b)=>{return a.name.toLowerCase().localeCompare(b.name.toLowerCase());});
            const matchedFonst = joinedFontsLists.filter(font => (font.name.toUpperCase().indexOf(props.searchValue!.toUpperCase())!==-1));
            setFontsFound(matchedFonst);
        }
        else {
            setFontsFound([]);
        }
    }, [commonFonts,oemFonts,props.isSearching,props.searchValue])

    const SetFontFamily = (fontName: string) => {
        const font = findFontByNameOrId([...(oemFonts ?? []), ...(commonFonts ?? [])], "id", fontName);
        if (font) {
          setFontFamily(font);
          context.onCommandChange("fontFamily", font.id);
        }     
    }

    return (
        <S.Root
            onScroll={optionsOnScroll}
        >
        <S.TopBottomSpace/>
            {
                !props.isSearching && 
                <>
                    {oem!=="Other" && oem!=="Generic" && oemFonts && 
                        <S.OEMFontsContainer>
                            { oemFonts.map(oemFontInfo => { 
                                
                                if (resultsMaxLength && fontsCounter < resultsMaxLength &&  oemFontInfo.fonts?.length>0) 
                                    return <><S.Title>
                                            {oemFontInfo.name} Fonts
                                        </S.Title>
                                        {
                                            oemFontInfo.fonts.map(oemFont => {
                                                fontsCounter ++;
                                                if (resultsMaxLength && fontsCounter < resultsMaxLength) return (
                                                    <ToolTip
                                                    tip={oemFont.name}
                                                    
                                                    >
                                                    <S.FontItem 
                                                      fontFamily={`font-${oemFont.id}`} 
                                                      isSelected={oemFont.id === fontFamily?.id} 
                                                      onClick={() => SetFontFamily(oemFont.id)}
                                                    >
                                                        { oemFont.name }
                                                    </S.FontItem>
                                                    </ToolTip>
                                                )
                                                else return;
                                            })
                                        }</>
                                else return;
                            })}
                            
                        </S.OEMFontsContainer>
                    }
                    { notOEMFonts && notOEMFonts.length>0 &&  
                        <>
                        <S.Separator></S.Separator>
                        <S.OEMFontsContainer>
                            { notOEMFonts.map( notOEMFont => { 
                                
                                if (resultsMaxLength && fontsCounter < resultsMaxLength &&  notOEMFont.fonts?.length>0) 
                                    return <><S.Title>
                                            {notOEMFont.name} Fonts
                                        </S.Title>
                                        {
                                            notOEMFont.fonts.map(brandFont => {
                                                fontsCounter ++;
                                                if (resultsMaxLength && fontsCounter < resultsMaxLength) return (
                                                    <S.FontItem 
                                                      fontFamily={`font-${brandFont.id}`} 
                                                      isSelected={brandFont.id === fontFamily?.id} 
                                                      onClick={() => SetFontFamily(brandFont.id)}
                                                    >
                                                        { brandFont.name }
                                                    </S.FontItem>
                                                )
                                                else return;
                                            })
                                        }</>
                                else return;
                            })}
                            
                        </S.OEMFontsContainer>
                        </>
                    }
                    { (resultsMaxLength && fontsCounter < resultsMaxLength) && 
                        commonFonts?.length && filterGenericFontsListForMaxLenght(oemFonts, notOEMFonts, commonFonts).length>0 &&  
                        <>
                        <S.Separator></S.Separator>
                        <S.CommonFontsContainer>
                            <S.Title>
                              {commonFonts[0].name} Fonts
                            </S.Title>
                            {  commonFonts && commonFonts[0].fonts.map((genericFont) => {
                                    if (resultsMaxLength && fontsCounter < resultsMaxLength) {
                                        fontsCounter ++;
                                        return (
                                            <ToolTip
                                                    tip={genericFont.name}                                                    
                                                    >
                                            <S.FontItem 
                                              fontFamily={`font-${genericFont.id}`} 
                                              isSelected={genericFont.id === fontFamily?.id}
                                              onClick={() => SetFontFamily(genericFont.id)}
                                            >
                                                { genericFont.name }
                                            </S.FontItem>
                                            </ToolTip>
                                        )
                                    } else return;
                                })
                            }
                        </S.CommonFontsContainer>
                        </>
                    }
                </>
            }
            {
                props.isSearching && 
                <>
                    {
                        fontsFound.length>0 && 
                        filterFontsFoundForMaxLenght(fontsFound).map((fontFound) => {
                            return (
                                <S.FontItem 
                                  fontFamily={`font-${fontFound.id}`} 
                                  isSelected={fontFound.id === fontFamily?.id} 
                                  onClick={() => SetFontFamily(fontFound.id)}
                                >
                                    { fontFound.name }
                                </S.FontItem>
                            )
                        })
                    }
                    {
                        fontsFound.length===0 && 
                        <S.Message>
                            No results found for this font 
                        </S.Message>
                    }
                </>
            }
        <S.TopBottomSpace/>
        </S.Root>
    );
}

export default FontFamilyPicker;